import TxFlow, { TxnStatuses } from "components/TxFlow/TxFlow";
import { sendEvent, AnalyticsEvent } from "analytics/analytics";
import { setIsTxnPending, setTxnHash } from "state/antepools/antepools";
import SnackbarUtils from "./SnackbarUtils";
import { ethers, ContractReceipt, BigNumber, Contract, Signer } from "ethers";
import { TransactionResponse, Web3Provider } from "@ethersproject/providers";
import { getNetworkById, toHex } from "./utils";
import { store } from "state";
import IERC20Abi from "abi/IERC20.json";

export type ContractCall = {
  method: Function;
  transform?: (value: any) => any;
  valueName?: string;
  skip?: boolean;
};

const WEB3_ERROR_VALUE = 3.9638773911973445e75;

const call = (method: ContractCall) => {
  return new Promise(async (resolve, reject) => {
    const {
      method: contractMethod,
      transform = (value: any) => value,
      valueName,
      skip = false,
    } = method;
    if (skip) {
      return resolve({ valueName, transformedValue: undefined });
    }

    try {
      const result = await contractMethod();

      if (Number(result) === WEB3_ERROR_VALUE) {
        console.error(`${contractMethod.name}.call`, "Contract call failure!");
        return reject(method);
      }

      let transformedValue: any = undefined;
      try {
        transformedValue = transform(result);
      } catch (error) {
        console.warn(`transforming ${valueName} has failed for: `, result);
      }

      return resolve({ valueName, transformedValue });
    } catch (error) {
      console.error(`${contractMethod.toString()} call failed`, error);
      return reject(method);
    }
  });
};

const batch = async (methods: ContractCall[]): Promise<any> => {
  const promises = methods.map((method) => call(method));

  const fetchedData = {};
  let failedMethods = [];

  const results = await Promise.allSettled(promises);
  const handleResult = (result) => {
    if (result.status === "fulfilled") {
      const { valueName, transformedValue } = result.value;
      fetchedData[valueName] = transformedValue;
    } else {
      failedMethods.push(result.reason);
    }
  };
  results.forEach((result) => {
    handleResult(result);
  });

  let retries = 5;
  while (failedMethods.length > 0) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    if (retries === 0) {
      throw new Error("failed to fetch, retries exceeded");
    }
    const retryPromises = failedMethods.map((method) => call(method));
    failedMethods = [];
    const retriedResults = await Promise.allSettled(retryPromises);
    retriedResults.forEach((result) => handleResult(result));
    retries--;
  }
  return fetchedData;
};

export type SendOptions = {
  toastMessage: string;
  analyticsProperties?: AnalyticsEvent;
  onTransactionHash?: (hash: string) => void;
  onReceipt?: (receipt: ContractReceipt) => void;
  onError?: (error: Error, transactionHash: string) => void;
};

const send = async (
  method: () => Promise<TransactionResponse>,
  options: SendOptions,
  provider: ethers.providers.Provider
): Promise<ContractReceipt> => {
  return new Promise(async (resolve, reject) => {
    const chain = getNetworkById(toHex((await provider.getNetwork()).chainId));
    const onError = (err: Error, transactionHash?: string) => {
      if (options.onError) {
        options.onError(err, transactionHash);
      }

      store.dispatch(setIsTxnPending(false));
      if (transactionHash) {
        SnackbarUtils.toast(options.toastMessage, {
          autoHideDuration: 10000,
          content: (key, message) => (
            <TxFlow
              message={message}
              txnHash={transactionHash}
              status={TxnStatuses.Reverted}
              closeSnackbar={() => SnackbarUtils.close(key)}
              chain={chain}
            />
          ),
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      }
    };

    const onTransactionHash = (transactionHash: string) => {
      if (options.onTransactionHash) {
        options.onTransactionHash(transactionHash);
      }

      store.dispatch(setIsTxnPending(true));
      store.dispatch(setTxnHash(transactionHash));
      SnackbarUtils.toast(options.toastMessage, {
        autoHideDuration: 10000,
        content: (key, message) => (
          <TxFlow
            message={message}
            txnHash={transactionHash}
            status={TxnStatuses.Pending}
            closeSnackbar={() => SnackbarUtils.close(key)}
            chain={chain}
          />
        ),
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
      if (options.analyticsProperties) {
        const { eventName, options: analyticsOptions } =
          options.analyticsProperties;
        sendEvent(eventName, { ...analyticsOptions, transactionHash });
      }
    };

    const onReceipt = (receipt: ContractReceipt) => {
      if (options.onReceipt) {
        options.onReceipt(receipt);
      }

      store.dispatch(setIsTxnPending(false));
      const { status, transactionHash } = receipt;
      if (status) {
        SnackbarUtils.toast(options.toastMessage, {
          autoHideDuration: 10000,
          content: (key, message) => (
            <TxFlow
              message={message}
              txnHash={transactionHash}
              status={TxnStatuses.Confirmed}
              closeSnackbar={() => SnackbarUtils.close(key)}
              chain={chain}
            />
          ),
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      }
    };

    let tx: TransactionResponse;
    try {
      tx = await method();
      onTransactionHash(tx.hash);

      const receipt = await tx.wait();
      onReceipt(receipt);
      resolve(receipt);
    } catch (error: any) {
      window.rollbar.warn("Transaction error", error);
      console.error("Transaction error", error);
      if (tx !== undefined) {
        onError(error, tx.hash);
      }
      return reject(error);
    }
  });
};

const getWeb3Signer = async (
  provider: any,
  account: string
): Promise<Signer> => {
  if (provider.getSigner) {
    return await provider.getSigner(account);
  } else {
    return new ethers.providers.Web3Provider(provider).getSigner(account);
  }
};

const approveERC20Tokens = async (
  tokenAddress: string,
  spender: string,
  amount: BigNumber,
  provider: Web3Provider
) => {
  const approve = () => {
    const signer = provider.getSigner();
    const contract = new Contract(tokenAddress, IERC20Abi, signer);
    return contract.approve(spender, amount);
  };

  await Web3Utils.send(
    approve,
    {
      toastMessage: "Approving...",
    },
    provider
  );
};

export const Web3Utils = {
  send,
  batch,
  call,
  getWeb3Signer,
  approveERC20Tokens,
};
